import { Flex } from 'components/shared/flex'
import { usePostDiscountApi, usePutDiscountApi } from 'api/discount'
import { InputField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useState } from 'react'
import { Alert, Button, Modal } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { Discount, DiscountForm, DISCOUNT_KIND_SELECT_ITEMS } from 'entities/discount'

export type FormViewProps = {
    form: Form<DiscountForm>
    apiError: ApiError
}

const FormView = ({ form, apiError }: FormViewProps) => {
    useEffectSkipFirst(() => {
        form.update((f) => {
            if (f.kind === 'fixed') {
                delete f.monthPeriod
                delete f.amount
            } else {
                delete f.value
                delete f.amount
            }
        })
    }, [form.object.kind])

    return (
        <>
            <Flex>
                <InputField form={form} attr={'name'} apiError={apiError} label="名前" required />
                <SelectField form={form} attr={'kind'} apiError={apiError} label="タイプ" selectItems={DISCOUNT_KIND_SELECT_ITEMS} required includeBlank />
            </Flex>
            <Flex>
                {form.object.kind !== 'fixed' ? (
                    <InputField
                        form={form}
                        attr={'amount'}
                        apiError={apiError}
                        label="数量"
                        type="number"
                        hint={form.object.kind === 'volume' ? 'n検体/月を達成で卸価格から割引(※検体到着日基準)' : '最初のn検体まで卸価格から割引'}
                    />
                ) : (
                    <></>
                )}
            </Flex>

            <Flex>
                <InputField form={form} attr={'ratio'} apiError={apiError} label="割引率" type="number" min={0} max={1} step={0.01} />
                <InputField form={form} attr={'value'} apiError={apiError} label="割引額" type="number" min={0} />
            </Flex>
            <div style={{ fontSize: 12 }}>※ 割引率、割引額の併用可能</div>

            {(form.object.value || form.object.ratio) && (
                <>
                    {(() => {
                        const discount = [
                            form.object.value ? `${form.object.value?.toLocaleString()}円/${form.object.kind === 'trial' ? '検体' : '月'}` : null,
                            form.object.ratio ? `卸価格の${Math.round(form.object.ratio! * 100)}％` : null,
                        ]
                            .filter((i) => !!i)
                            .join(' 及び ')
                        if (form.object.kind === 'fixed') {
                            return (
                                <Alert>
                                    {`${discount}`}割引{form.object.monthPeriod ? `(${form.object.monthPeriod}ヶ月間)` : '(無期限)'}
                                </Alert>
                            )
                        } else if (form.object.kind === 'volume') {
                            return (
                                <Alert>
                                    {form.object.amount}検体/月を達成で{`${discount}`}割引
                                    {form.object.monthPeriod ? `(${form.object.monthPeriod}ヶ月間)` : '(無期限)'}
                                </Alert>
                            )
                        } else if (form.object.kind === 'trial') {
                            return (
                                <Alert>
                                    最初の{form.object.amount}検体まで{discount}割引
                                </Alert>
                            )
                        }
                    })()}
                </>
            )}
        </>
    )
}

type NewDiscountFormProps = {
    onComplete?: () => void
}

export const NewDiscountForm = (props: NewDiscountFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostDiscountApi()
    const form = useForm<DiscountForm>({})
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>割引の登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>
                        <FormView form={form} apiError={api.apiError} />
                    </>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
type EditDiscountFormProps = {
    discount: Discount
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditDiscountForm = (props: EditDiscountFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutDiscountApi()
    const form = useForm<DiscountForm>(props.discount)

    useEffectSkipFirst(() => {
        form.set(props.discount)
    }, [props.discount])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>割引の編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <FormView form={form} apiError={api.apiError} />}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
